import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { useAppConfigAuditQuery } from "../services/useAppConfigAuditQuery";

type Props = {
  configKey: string;
};

// Pure display: parent controls when this is mounted (hidden / shown).
// We always issue the audit query while mounted so opening shows fresh
// data. Backed by React Query, so flipping open/closed quickly is cheap.
export function AppConfigAuditPanel({ configKey }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const { data, isLoading } = useAppConfigAuditQuery(configKey, true);

  if (isLoading) {
    return (
      <p className="text-xs text-[color:var(--text-muted)]">
        {t(keys.common.loading)}
      </p>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="text-xs text-[color:var(--text-muted)]">
        {t(keys.appConfigs.historyEmpty)}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5 text-xs">
      {data.map((entry) => (
        <li
          key={entry.id}
          className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[color:var(--text-secondary)]"
        >
          <span className="font-mono text-[10px] text-[color:var(--text-muted)]">
            {formatTimestamp(entry.changedAt, locale)}
          </span>
          <span>
            <span className="font-mono">{formatAuditValue(entry.oldValue)}</span>
            <span className="mx-1.5 text-[color:var(--text-muted)]">→</span>
            <span className="font-mono font-semibold">
              {formatAuditValue(entry.newValue)}
            </span>
          </span>
          {entry.actorEmail ? (
            <span className="text-[10px] text-[color:var(--text-muted)]">
              {entry.actorEmail}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

// `null` means either "no prior value" (first set) or "reset to default"
// (delete). The empty-set glyph is conventional for both.
function formatAuditValue(value: string | null): string {
  return value ?? "∅";
}

function formatTimestamp(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale);
}
